import type {
  BabyQuote,
  CatQuote,
  ChildQuote,
  DogQuote,
  HouseholderQuote,
  OfferViewModel,
  ProductType,
  Quote,
  QuoteCoverage,
  TravelQuote,
  VanQuote,
} from "@/api_client";

export function findQuote<T extends Quote>(
  offer: OfferViewModel | undefined | null,
  id: string | undefined | null,
): T | undefined {
  if (!offer?.quotes || !id) {
    return undefined;
  }
  return offer.quotes.find((p) => p.id === id) as T;
}

type ProductTypeKey = ProductType | "";

type ProductQuoteByType = Record<ProductTypeKey, Quote | undefined | null> & {
  travel: TravelQuote;
  householder: HouseholderQuote;
  baby: BabyQuote;
  car: Quote;
  income: Quote;
  sport: Quote;
  child: ChildQuote;
  cat: CatQuote;
  dog: DogQuote;
  van: VanQuote;
};

type ProductValueOrFactory<T> = T | (() => T);

type ProductQuoteHandler<T, K extends ProductTypeKey> =
  | T
  | ((quote: ProductQuoteByType[K]) => T);

type ProductTypeHandlerMap<T> = Record<
  ProductTypeKey,
  ProductValueOrFactory<T>
>;
type ProductQuoteHandlerMap<T> = {
  [K in ProductTypeKey]: ProductQuoteHandler<T, K>;
};

export function matchProductType<T>(
  type: ProductType | undefined | null,
  handlers: ProductTypeHandlerMap<T>,
): T;
export function matchProductType<T>(
  quote: Quote | undefined | null,
  handlers: ProductQuoteHandlerMap<T>,
): T;
export function matchProductType<T>(
  typeOrQuote: ProductType | Quote | undefined | null,
  handlers: ProductTypeHandlerMap<T> | ProductQuoteHandlerMap<T>,
): T {
  const isQuoteLike =
    typeof typeOrQuote === "object" &&
    typeOrQuote !== null &&
    "productType" in typeOrQuote;
  let productType: ProductTypeKey;

  if (isQuoteLike) {
    productType = typeOrQuote.productType ?? "";
  } else {
    productType = (typeOrQuote as ProductType | undefined | null) ?? "";
  }

  const handlerMap = handlers as Record<
    ProductTypeKey,
    ProductTypeHandlerMap<T>[ProductTypeKey]
  >;

  const handler = handlerMap[productType];

  if (typeof handler !== "function") {
    return handler;
  }

  return isQuoteLike
    ? (handler as (quote: Quote) => T)(typeOrQuote)
    : (handler as () => T)();
}

/// This makes a difference between no offer or id (undefined) and product not found (ProductNotFound)
/// basically if you have an id and an offer, you should get either a product or ProductNotFound
export function resolveQuote<T extends Quote>(
  id: string | undefined,
  offer: OfferViewModel | undefined,
): T | undefined {
  if (!offer?.quotes || !id) return undefined;
  const quote = findQuote<T>(offer, id);
  return quote;
}

export function updateCoverage(
  offer: OfferViewModel,
  productId: string,
  coverageName: string,
  mutationFn: (coverage: QuoteCoverage) => QuoteCoverage,
): OfferViewModel {
  return {
    ...offer,
    quotes: offer.quotes?.map((quote) => {
      if (quote.id !== productId) {
        return quote;
      }
      return {
        ...quote,
        coverages: quote.coverages?.map((coverage) => {
          if (coverage.coverageName !== coverageName) {
            return coverage;
          }
          const changedCoverage = structuredClone(coverage);
          return mutationFn(changedCoverage);
        }),
      };
    }),
  };
}
